// Title screen ("front door"): dusk backdrop, wordmark, Continue / New Game / Load / Settings / Credits.
// runTitle({ apiReady, api, skip }) -> Promise<{ state, fresh }>. The overlay stays up (over the game as it
// mounts) until dismiss() is called, then fades out. newGameFlow(api) runs charter + intro over a live game.
import { esc, money } from './dom.js';
import { formatDate } from '../core/time.js';
import { emblemSVG } from './emblem.js';
import { openCharter, showIntro, createGame } from './newGame.js';
import { createDuskScene } from './titleScene.js';
import { audio } from './audioBridge.js';
import { pwa } from './pwa.js';
// perf(load): audio credits (src/audio/sampledata.js, ~48 KB) load when the Credits sheet opens, not with the title.
let AUDIO_CREDITS = [];
const loadCredits = () => (AUDIO_CREDITS.length ? Promise.resolve() : import('../audio/sampledata.js').then((m) => { AUDIO_CREDITS = m.CREDITS ?? []; }).catch((e) => console.warn('[title] audio credits unavailable', e)));

const VERSION = '0.1';
const RESUME_KEY = 'driller.resume';
const MOTION_KEY = 'driller.motion';
const THEME_KEY = 'driller.theme';
const MARK = `<svg viewBox="0 0 32 32" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" stroke-linecap="round"><path d="M16 3 9 29M16 3l7 26M11.5 20h9M10.2 25h11.6M13 13.5h6"/><path d="M12 13.5 20.5 20M20 13.5 11.5 20M11 20l10.2 5M21 20 10.2 25" stroke-width="1" opacity=".8"/><path d="M5 29h22"/></g><circle cx="16" cy="3" r="1.5" fill="currentColor"/></svg>`;

// cut scenes: the 1859 opening plays before the prologue card (skippable; Menu → Settings turns them off)
const openingScene = async (opts) => { if ((opts.startYear ?? 1859) >= 1901) return; try { const { playCutscene } = await import('./cutscene.js'); await playCutscene('intro1859'); } catch { /* skip */ } };
let el = null, scene = null, keyHandler = null;

export const reducedMotion = () => {
  try { if (localStorage.getItem(MOTION_KEY) === 'reduce') return true; } catch {}
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
};
export function applyPrefs() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    if (t) document.documentElement.dataset.theme = t; else delete document.documentElement.dataset.theme;
    if (localStorage.getItem(MOTION_KEY) === 'reduce') document.documentElement.dataset.motion = 'reduce'; else delete document.documentElement.dataset.motion;
  } catch {}
}

function mountOverlay() {
  el = document.createElement('div');
  el.className = 'fd';
  el.innerHTML = `<canvas class="fd-bg" aria-hidden="true"></canvas><div class="fd-shade" aria-hidden="true"></div><div class="fd-stage"></div>`;
  document.body.appendChild(el);
  // keep keys away from the game's global shortcuts (Space = pause, digits = speed, letters = panels)
  el.addEventListener('keydown', (e) => { e.stopPropagation(); keyHandler?.(e); });
  scene = createDuskScene(el.querySelector('.fd-bg'), { reducedMotion: reducedMotion() });
  scene.start();
  const app = document.getElementById('app');
  if (app) app.inert = true;
  return el.querySelector('.fd-stage');
}

export function dismiss() {
  if (!el) return;
  const node = el, sc = scene;
  el = null; scene = null; keyHandler = null;
  const app = document.getElementById('app');
  if (app) app.inert = false;
  audio.stopTitleMusic();
  const fin = () => { sc?.dispose(); node.remove(); document.getElementById('main')?.focus({ preventScroll: true }); };
  if (reducedMotion()) return fin();
  node.classList.add('fd-out');
  setTimeout(fin, 650);
}

// ---------- saves ----------
function latest(api) {
  try {
    let s = api.save.loadGame?.('latest') ?? null;
    if (!s) s = api.save.loadGame?.('auto') ?? null;
    if (s && api.save.migrate) s = api.save.migrate(s);
    return s?.company ? s : null;
  } catch (e) { console.warn('[title] autosave unreadable', e); return null; }
}
function slotLabel(slot) {
  const s = String(slot);
  if (s === 'auto' || s.startsWith('auto')) return 'Autosave';
  return 'Slot ' + s;
}
function ago(ts) {
  if (!ts) return '';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(ts).toLocaleDateString();
}

// ---------- main ----------
export async function runTitle({ apiReady, api, skip = false }) {
  applyPrefs();
  let resume = false;
  try { resume = sessionStorage.getItem(RESUME_KEY) === '1'; sessionStorage.removeItem(RESUME_KEY); } catch {}
  if (skip || resume) {
    await apiReady;
    const s = latest(api);
    return { state: s ?? createGame(api, {}), fresh: !s };
  }

  const stage = mountOverlay();
  stage.innerHTML = `<div class="fd-menu-view">
      <header class="fd-brand">
        <div class="fd-est"><span></span>Est. 1859<span></span></div>
        <h1 class="fd-logo" aria-label="Landman">LANDMAN</h1>
        <p class="fd-tag">From Titusville to Titan</p>
      </header>
      <div class="fd-body">
        <nav class="fd-menu" aria-label="Main menu" data-role="menu">
          <div class="fd-loading">Spudding in…</div>
        </nav>
        <section class="fd-sheet" data-role="sheet" hidden></section>
      </div>
      <footer class="fd-foot">
        <span>v${VERSION} · Early access</span>
        <a href="./welcome/">About the game</a>
        <button type="button" class="fd-linkbtn" data-act="install" hidden>Install app</button>
      </footer>
    </div>`;
  const menu = stage.querySelector('[data-role=menu]');
  const sheet = stage.querySelector('[data-role=sheet]');
  const installBtn = stage.querySelector('[data-act=install]');
  import('./tips.js').then((m) => m.titleTip(stage.querySelector('.fd-foot'))).catch(() => {}); // tutorial: rotating foreman's tip line
  pwa.onInstallable((ok) => { installBtn.hidden = !ok; });
  installBtn.addEventListener('click', () => pwa.promptInstall());

  await apiReady;
  const save = latest(api);
  audio.detect(); // perf(load): fetch the sound engine once the menu can render, so it doesn't compete with the sim chunk

  return new Promise((resolve) => {
    const finish = (v) => { keyHandler = null; resolve(v); };
    const items = [];
    if (save) items.push({ id: 'continue', label: 'Continue', sub: true });
    items.push({ id: 'new', label: 'New Game' }, { id: 'load', label: 'Load' }, { id: 'settings', label: 'Settings' }, { id: 'credits', label: 'Credits' });
    menu.innerHTML = items.map((it) => {
      if (it.id !== 'continue') return `<button type="button" class="fd-item" data-item="${it.id}"><span>${it.label}</span></button>`;
      const c = save.company;
      return `<button type="button" class="fd-item fd-continue" data-item="continue">
        <span class="fd-cont-em">${emblemSVG(c.logo, { size: 44, title: c.name })}</span>
        <span class="fd-cont-txt"><span>Continue</span><small>${esc(c.name)} · ${esc(formatDate(save.day))} · ${esc(money(c.cash))}</small></span></button>`;
    }).join('');
    const buttons = [...menu.querySelectorAll('.fd-item')];
    buttons[0].focus();

    let openId = null;
    const closeSheet = () => {
      sheet.hidden = true; sheet.innerHTML = '';
      const b = buttons.find((x) => x.dataset.item === openId);
      for (const x of buttons) x.removeAttribute('aria-expanded');
      openId = null;
      stage.querySelector('.fd-menu-view').classList.remove('sheet-open');
      b?.focus();
    };
    const openSheet = (id, htmlStr, wire) => {
      openId = id;
      for (const x of buttons) x.toggleAttribute('aria-expanded', x.dataset.item === id);
      sheet.hidden = false;
      sheet.innerHTML = `<button type="button" class="fd-x" data-act="close" aria-label="Close">✕</button>${htmlStr}`;
      sheet.querySelector('[data-act=close]').addEventListener('click', closeSheet);
      stage.querySelector('.fd-menu-view').classList.add('sheet-open');
      wire?.(sheet);
      (sheet.querySelector('[data-autofocus]') ?? sheet.querySelector('button:not(.fd-x), input, select, a') ?? sheet.querySelector('.fd-x'))?.focus();
    };

    const startNew = async () => {
      audio.play('click');
      const view = stage.querySelector('.fd-menu-view');
      view.hidden = true;
      const host = document.createElement('div');
      host.className = 'fd-flow';
      stage.appendChild(host);
      keyHandler = null;
      const opts = await openCharter(host, {});
      if (!opts) { host.remove(); view.hidden = false; keyHandler = menuKeys; buttons.find((b) => b.dataset.item === 'new')?.focus(); return; }
      audio.play('gavel');
      scene?.setMood('intro');
      await openingScene(opts);
      await showIntro(host, opts, { reducedMotion: reducedMotion() });
      finish({ state: createGame(api, opts), fresh: true });
    };

    const activate = (id) => {
      if (id === 'continue') { audio.play('click'); return finish({ state: save, fresh: false }); }
      if (id === 'new') return startNew();
      if (openId === id) return closeSheet();
      audio.play('click');
      if (id === 'load') return openSheet('load', loadSheetHtml(api), (sh) => wireLoad(sh, api, (s) => finish({ state: s, fresh: false })));
      if (id === 'settings') return openSheet('settings', settingsHtml(), wireSettings);
      if (id === 'credits') return loadCredits().then(() => openSheet('credits', creditsHtml())); // perf(load)
    };
    menu.addEventListener('click', (e) => { const b = e.target.closest('[data-item]'); if (b) activate(b.dataset.item); });

    const menuKeys = (e) => {
      const i = buttons.indexOf(document.activeElement);
      if (e.key === 'Escape' && openId) { e.preventDefault(); closeSheet(); return; }
      if (i < 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        buttons[(i + (e.key === 'ArrowDown' ? 1 : buttons.length - 1)) % buttons.length].focus();
      } else if (e.key === 'Home') { e.preventDefault(); buttons[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); buttons.at(-1).focus(); }
      else if (e.key === 'ArrowRight' && openId) { e.preventDefault(); sheet.querySelector('button:not(.fd-x), input, select, a')?.focus(); }
    };
    keyHandler = menuKeys;
    // first gesture unlocks audio (autoplay policy) and starts title music if the engine has it
    const unlock = () => { audio.titleMusic(); };
    menu.addEventListener('focusin', (e) => { if (e.target.closest('.fd-item')) audio.play('hover'); });
    el.addEventListener('pointerdown', unlock, { once: true });
    el.addEventListener('keydown', unlock, { once: true });
  });
}

// In-game "New game…" (from the Menu modal / game over): charter + intro over the running game.
export async function newGameFlow(api) {
  if (el) return null;
  const stage = mountOverlay();
  const host = document.createElement('div');
  host.className = 'fd-flow';
  stage.appendChild(host);
  const opts = await openCharter(host, {});
  if (!opts) { dismiss(); return null; }
  audio.play('gavel');
  scene?.setMood('intro');
  await openingScene(opts);
  await showIntro(host, opts, { reducedMotion: reducedMotion() });
  const s = createGame(api, opts);
  dismiss();
  return s;
}

// ---------- sheets ----------
function loadSheetHtml(api) {
  let list = [];
  try { list = api.save.listSaves?.() ?? []; } catch {}
  const rows = list.length ? list.map((m) => `
    <li class="fd-slot">
      <div><b>${esc(m.company ?? m.name ?? m.companyName ?? 'Company')}</b>
        <small>${esc(slotLabel(m.slot))}${m.day != null ? ' · ' + esc(formatDate(m.day)) : ''}${m.cash != null ? ' · ' + esc(money(m.cash)) : ''}${m.savedAt ? ' · ' + esc(ago(m.savedAt)) : ''}</small></div>
      <button type="button" class="fd-btn sm" data-load="${esc(m.slot)}">Load</button>
    </li>`).join('') : '<li class="fd-empty">No saved companies yet.</li>';
  return `<h2 class="fd-h3">Load a company</h2>
    <ul class="fd-slots">${rows}</ul>
    <div class="fd-sep"></div>
    <h3 class="fd-h4">Import a save file</h3>
    <p class="fd-muted">A <code>.json</code> exported from the in-game Menu.</p>
    <label class="fd-btn sm fd-file"><input type="file" accept=".json,application/json" data-role="file">Choose file…</label>
    <p class="fd-err" data-role="err" role="alert"></p>`;
}
function wireLoad(sh, api, done) {
  const err = sh.querySelector('[data-role=err]');
  sh.addEventListener('click', (e) => {
    const b = e.target.closest('[data-load]');
    if (!b) return;
    try {
      let s = api.save.loadGame?.(b.dataset.load);
      if (s && api.save.migrate) s = api.save.migrate(s);
      if (!s?.company) throw new Error('That save could not be read.');
      done(s);
    } catch (ex) { err.textContent = ex.message; }
  });
  sh.querySelector('[data-role=file]').addEventListener('change', async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const txt = await f.text();
      const s = api.save.importSave ? api.save.importSave(txt) : JSON.parse(txt);
      if (!s?.company) throw new Error('Not a Landman save.');
      done(s);
    } catch (ex) { err.textContent = 'Import failed: ' + ex.message; }
  });
}

function settingsHtml() {
  let theme = '', motion = '';
  try { theme = localStorage.getItem(THEME_KEY) ?? ''; motion = localStorage.getItem(MOTION_KEY) ?? ''; } catch {}
  const a = audio.settings();
  const seg = (name, opts, cur) => `<div class="fd-seg" role="radiogroup" aria-label="${name}">${opts.map(([v, l]) => `<label><input type="radio" name="${name}" value="${v}" ${cur === v ? 'checked' : ''}><span>${l}</span></label>`).join('')}</div>`;
  const slider = (bus, label) => `<label class="fd-slider"><span>${label}</span><input type="range" min="0" max="100" step="1" data-bus="${bus}" value="${Math.round((a[bus] ?? 0) * 100)}"><output>${Math.round((a[bus] ?? 0) * 100)}</output></label>`;
  return `<h2 class="fd-h3">Settings</h2>
    <h3 class="fd-h4">Ledger theme</h3>
    <p class="fd-muted">Colours for the in-game panels.</p>
    ${seg('theme', [['', 'System'], ['dark', 'Dusk'], ['light', 'Parchment']], theme)}
    <h3 class="fd-h4">Sound</h3>
    ${slider('master', 'Master')}${slider('music', 'Music')}${slider('sfx', 'Effects')}${slider('ambience', 'Ambience')}
    <label class="fd-check"><input type="checkbox" data-role="mute" ${a.muted ? 'checked' : ''}> Mute all</label>
    <p class="fd-muted" data-role="astatus">${audio.ready() ? 'Sound engine connected.' : 'Sound engine is still being installed — your levels are saved and will apply when it arrives.'}</p>
    <h3 class="fd-h4">Motion</h3>
    ${seg('motion', [['', 'Follow system'], ['reduce', 'Reduce motion']], motion)}`;
}
function wireSettings(sh) {
  sh.addEventListener('change', (e) => {
    const t = e.target;
    try {
      if (t.name === 'theme') { if (t.value) localStorage.setItem(THEME_KEY, t.value); else localStorage.removeItem(THEME_KEY); applyPrefs(); }
      if (t.name === 'motion') { if (t.value) localStorage.setItem(MOTION_KEY, t.value); else localStorage.removeItem(MOTION_KEY); applyPrefs(); }
    } catch {}
    if (t.dataset.role === 'mute') audio.set({ muted: t.checked });
  });
  sh.addEventListener('input', (e) => {
    const t = e.target;
    if (!t.dataset.bus) return;
    t.nextElementSibling.textContent = t.value;
    audio.set({ [t.dataset.bus]: t.value / 100 });
  });
  audio.whenReady(() => { const s = sh.querySelector('[data-role=astatus]'); if (s) s.textContent = 'Sound engine connected.'; });
}

function creditsHtml() {
  return `<h2 class="fd-h3">Credits</h2>
    <div class="fd-credits">
      <p><b>Landman</b> — an oil-drilling tycoon from Titusville, 1859, to the moons of Saturn.</p>
      <dl>
        <dt>Design, code &amp; art</dt><dd>The Landman team</dd>
        <dt>Historical oil prices</dt><dd>U.S. EIA, BP Statistical Review of World Energy, and period newspapers (annual averages, rounded)</dd>
        <dt>Inflation &amp; wages</dt><dd>U.S. CPI series and historical wage studies</dd>
        <dt>Type</dt><dd>Zilla Slab (Typotheque for Mozilla) and IBM Plex (IBM), via Google Fonts</dd>
        <dt>3D</dt><dd>Three.js</dd>
      </dl>
      ${musicCreditsHtml()}
      <p class="fd-muted">Dedicated to the roughnecks, tool dressers and teamsters of Oil Creek — and to everyone who has ever waited on a well.</p>
      <p class="fd-muted">Prices and events are historical but simplified; companies and characters in the game are fictional.</p>
    </div>`;
}

// Music attributions (CC BY 4.0 requires showing them) and a summary of the CC0 / public-domain sound sources.
function musicCreditsHtml() {
  const music = AUDIO_CREDITS.filter((c) => c.use === 'Music');
  const sfx = AUDIO_CREDITS.filter((c) => c.use !== 'Music');
  const link = (url, text) => (url ? `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(text)}</a>` : esc(text));
  const rows = music.map((c) => `<li><b>${esc(c.title)}</b> — ${esc(c.author)}<span class="fd-muted"> · ${link(c.licenseUrl, c.license)}</span>${c.attribution && /CC BY/i.test(c.license) ? `<br><span class="fd-muted small">${esc(c.attribution)}</span>` : ''}</li>`).join('');
  const sources = [...new Set(sfx.map((c) => c.source ?? c.site ?? '').filter(Boolean))];
  return `<h3 class="fd-h3" style="margin-top:14px">Music</h3><ul class="fd-credit-list">${rows}</ul>
    <h3 class="fd-h3" style="margin-top:14px">Sound recordings</h3>
    <p class="fd-muted">${sfx.length} recordings released into the public domain (CC0) or public-domain archival recordings${sources.length ? ` from ${esc(sources.slice(0, 6).join(', '))}` : ', from Freesound, Kenney and Wikimedia Commons'}. Thank you to every recordist who shares their work freely.</p>`;
}
